import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { filter, skipWhile } from 'rxjs/operators';

import { AdjustmentService } from '@core/api/adjustment.service';

import { PaymentService } from '@core/api/payment.service';
import { SiteService } from '@core/api/site.service';
import { UserService } from '@core/api/user.service';
import { SiteHelperService } from '@core/helper/site.service';
import * as fromApp from '@store';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { resetStripeAccountRequiresAction } from '@store/notification/notification.actions';
import { setActiveSite } from '@store/site/site.actions';
import { selectActiveSite } from '@store/site/site.selectors';
import { LegalConstants } from '@shared/constants/legal.constants';
import { AdjustValueDialogComponent } from '@shared/dialogs/adjust-value-dialog/adjust-value-dialog.component';
import { DialogConfirmPaymentComponent } from '@shared/dialogs/dialog-confirm-payment/dialog-confirm-payment.component';
import { DialogEditPaymentPeriodComponent } from '@shared/dialogs/dialog-edit-payment-period/dialog-edit-payment-period.component';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Adjustment, AdjustmentType, PaymentPeriod, Site, SitePayment } from '@shared/models';
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import moment from 'moment';

@Component({
  selector: 'fc-site-detail-payments',
  templateUrl: './site-detail-payments.component.html',
  styleUrls: ['./site-detail-payments.component.scss'],
  standalone: false
})
export class SiteDetailPaymentsComponent implements OnInit, AfterViewInit {
  destroyRef = inject(DestroyRef);

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input()
  isSuperAdmin = false;

  @Input()
  canPaySites = false;

  @Input()
  isLandowner = true;

  totalPayments = null;
  showManualPayment = false;
  hasStripe: boolean = false;
  expressPayment = false;
  newManualPayment: UntypedFormGroup;
  payments: SitePayment[] = null;
  error: string = null;
  maxPayment: number = null;

  allAdjustmentTypes: AdjustmentType[] = [];
  nextPaymentPeriods: PaymentPeriod[] = [];
  initialAdjustment: number = 0;
  stripeDisclaimer = LegalConstants.STRIPE_DISCLAIMER;
  valuation: string;
  totalPaymentPeriods: number = 0;
  site: Site;

  showAdjustValue: boolean = false;

  valueAdjustmentForm: UntypedFormGroup;
  adminType: AdjustmentType;

  currentUserId: number = null;
  initialPageIndex = 1;

  displayedColumns: string[] = ['accrual', 'net', 'ppt', 'total', 'payment', 'report'];
  dataSource = new MatTableDataSource<PaymentPeriod>();

  constructor(
    public dialog: MatDialog,
    readonly router: Router,
    readonly paymentService: PaymentService,
    readonly store: Store<fromApp.AppState>,
    readonly userService: UserService,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly formBuilder: UntypedFormBuilder,
    readonly adjustmentService: AdjustmentService
  ) {
    this.valueAdjustmentForm = this.formBuilder.group({
      amount: [0.0, Validators.required],
    });
  }

  ngOnInit() {

    this.store
      .select(selectCurrentUser)
      .pipe(
        filter(currentUser => currentUser !== null),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(user => {
      this.currentUserId = user.id;
    });
    this.store
      .select(selectActiveSite)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(site => site === null)
      )
      .subscribe(activeSite => {
        this.site = activeSite;
        let paymentPeriods = cloneDeep(this.site.paymentPeriods);
        this.dataSource.data = paymentPeriods.sort((a, b) => a.sequence - b.sequence);
        if (this.dataSource.data.length > 0) {
          let desiredPosition = (this.dataSource.data.filter(p => !p.payment))[0].sequence;
          this.initialPageIndex = Math.floor(desiredPosition / 5);
        }
        // if site payments have any adjustments, add adjusment column after base
        if (!this.displayedColumns.includes('adjustments') && this.site.paymentPeriods.some(p => p.adjustments.length > 0)) {
          this.displayedColumns.splice(1, 0, 'base', 'adjustments');
        }
        if (!this.displayedColumns.includes('payout_adjustment') && this.site.paymentPeriods.some(p => p.netPayoutAdjustment != 0)) {
          this.displayedColumns.splice(this.displayedColumns.length - 3, 0, 'payout_adjustment');
        }
        if (!this.displayedColumns.includes('actions') && this.isSuperAdmin) {
          this.displayedColumns.push('actions');
        }
        this.hasStripe = this.site.applicant.hasStripe();
        this.valuation = this.site.valuation
          ? (parseFloat(this.site.valuation.split(',')[0]) / (12 / this.site.enrollmentGroup.payPeriodCadence)).toLocaleString(
            undefined,
            {
              maximumFractionDigits: 2,
            }
          )
          : '0';
        this.paymentService.getHistory(this.site.id).subscribe(payments => {
          this.payments = payments.sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
          this.calculateTotalPayments();
        });

        this.adjustmentService.getAllAdjustmentTypes().subscribe(adjustmentTypes => {
          this.allAdjustmentTypes = adjustmentTypes;
          this.adminType = adjustmentTypes.find(at => at.shortName === 'admin_adjustment');
          this.setNextThreeAdjustments();
        });
      });
  }

  ngAfterViewInit() {
    this.paginator.pageIndex = this.initialPageIndex;
    this.dataSource.paginator = this.paginator;
  }

  setNextThreeAdjustments(): void {
    this.nextPaymentPeriods = this.site.paymentPeriods.filter(p => !p.paymentId);
    this.totalPaymentPeriods = this.nextPaymentPeriods.length;
    if (this.nextPaymentPeriods.length > 0) {
      this.nextPaymentPeriods.sort((a, b) => a.sequence - b.sequence);
      for (let i = 0; i < 4; i++) {
        if (this.initialAdjustment === 0) {
          const adminAdjustment = this.nextPaymentPeriods[i].adjustments.find(
            adjustment => adjustment.adjustmentTypeId === this.adminType.id
          );
          if (adminAdjustment !== undefined) {
            this.initialAdjustment = adminAdjustment.value;
          }
        }
      }
      this.maxPayment = this.nextPaymentPeriods[0].netPayout;
    }
  }

  getAdjustmentViewName(name: string): string {
    switch (name) {
      case 'firewood': {
        return 'Firewood';
      }
      case 'small_clearing': {
        return 'Clearing';
      }
      case 'approval_adjustment': {
        return 'Approval';
      }
      case 'promotion_adjustment': {
        return 'Promotion';
      }
      default: {
        return 'Adjustment';
      }
    }
  }

  getPaymentDisplay(payment: SitePayment): string {
    return payment.error ? '—' : `$${((payment.paidAmountInCents || 0) / 100).toFixed(2)}`;
  }

  calculateTotalPayments(): void {
    let total = 0;
    if (this.payments && this.payments.length > 0) {
      this.payments.forEach(p => (total += p.paidAmountInCents));
    }
    this.totalPayments = (total / 100).toLocaleString('us-EN', {
      style: 'currency',
      currency: 'USD',
    });
  }

  addManualPayment(express) {
    this.expressPayment = express;
    if (express) {
      // Manual payments are ad-hoc Stripe payments (via Express) that are NOT associated with any payment period
      this.newManualPayment = this.formBuilder.group({
        paymentDate: [new Date(), Validators.required],
        paymentAmount: ['', [Validators.required, Validators.max(this.maxPayment)]],
        paymentNote: ['', Validators.required],
      });
    } else {
      // External payments are ad-hoc non-Stripe payments that are NOT associated with any payment period
      this.maxPayment = null;
      this.newManualPayment = this.formBuilder.group({
        paymentDate: [new Date(), Validators.required],
        paymentAmount: ['', Validators.required],
        paymentNote: ['', Validators.required],
      });
    }
    this.error = null;
    this.showManualPayment = true;
  }

  handleNewPaymentButton(submit) {
    if (submit) {
      const data = {
        siteId: this.site.id,
        date: this.newManualPayment.get('paymentDate').value,
        amount: this.newManualPayment.get('paymentAmount').value,
        note: this.newManualPayment.get('paymentNote').value,
      };
      if (this.expressPayment) {
        let dialogData = {
          calcs: {
            totalPaid: data.amount,
            payments: [
              {
                siteId: this.site.id,
                name: this.site.name,
                amount: data.amount,
              },
            ],
          },
          alreadyPaid: 0,
        };

        let dialogRef = this.dialog.open(DialogConfirmPaymentComponent, {
          panelClass: 'fc-dialog-confirm-payment',
          data: dialogData,
          autoFocus: false,
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            let reqSub = this.expressPayment
              ? this.paymentService.expressPayment({ data })
              : this.paymentService.recordPayment({ data });
            reqSub.subscribe({
              next: response => {
                this.payments.push(response);
                this.calculateTotalPayments();
                this.error = null;
                this.showManualPayment = false;
                this.expressPayment = false;
              },
              error: err => {
                console.log(err.message);
                this.error = 'There was an error when adding your payment record. Please try again.';
              }
            });
          }
        });
      } else {
        this.paymentService.recordPayment({ data }).subscribe({
          next: response => {
            this.payments.push(response);
            this.calculateTotalPayments();
            this.error = null;
            this.showManualPayment = false;
            this.expressPayment = false;
          },
          error: err => {
            console.log(err.message);
            this.error = 'There was an error when adding your payment record. Please try again.';
          }
        });
      }
    } else {
      this.error = null;
      this.showManualPayment = false;
      this.expressPayment = false;
    }
  }

  getPaymentMessage(payment) {
    if (payment.message != null && payment.message.length > 0) {
      return `${payment.transactionId ? 'Stripe: ' : ''}${payment.message}`;
    }
    if (payment.transactionId) {
      return 'Stripe';
    }
    return null;
  }

  setValueAdjustmentInitialValue() {
    this.valueAdjustmentForm.setValue({
      amount: this.initialAdjustment.toFixed(4),
    });
  }

  adjustValue(el: HTMLElement): void {
    this.setValueAdjustmentInitialValue();
    this.showAdjustValue = true;
    if (el) {
      el.scrollIntoView();
    }
  }

  cancelAdjustValue(): void {
    this.setValueAdjustmentInitialValue();
    this.showAdjustValue = false;
  }

  submitAdjustValue(): void {
    const currentValue = parseFloat(this.valuation);
    const newValue: number = currentValue - this.initialAdjustment + parseFloat(this.amount);
    const dialogRef = this.dialog.open(AdjustValueDialogComponent, {
      panelClass: 'fc-adjust-value-dialog',
      data: {
        currentValue: currentValue.toFixed(4),
        newValue: newValue.toFixed(4),
        siteName: this.site.name,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(reason => {
      if (reason) {
        const value = this.amount;
        const newAdjustment = new Adjustment({
          value: value,
          adjustmentTypeId: this.adminType.id,
        });
        this.adjustmentService.createAdjustment(this.site.id, reason, newAdjustment).subscribe({
          next: () => {
            this.store.dispatch(setActiveSite({ id: this.site.id }));
          },
        });
      }
    });
  }

  doStripeConnect(): void {
    // do api call to create stripe account uuid and save to user
    this.userService.doStripeConnect(
      this.site.applicant.id
    );
  }

  getLoginLink(): void {
    this.store.dispatch(resetStripeAccountRequiresAction());
    this.paymentService.getMyLoginUrl();
  }

  get maxPaymentLabel() {
    return this.maxPayment
      ? `(max ${this.maxPayment.toLocaleString('us-EN', {
        style: 'currency',
        currency: 'USD',
      })})`
      : null;
  }

  get amount() {
    return this.valueAdjustmentForm.value.amount;
  }

  get stripeIsActive() {
    return this.site.applicant.stripeAccounts?.filter(sa => sa.stripeAccountCreationDate != null).length > 0;
  }

  get showStripeLink() {
    return this.currentUserId === this.site?.applicant.id && this.site?.activationDate;
  }

  editPeriod(pp: PaymentPeriod) {
    this.dialog
      .open(DialogEditPaymentPeriodComponent, {
        data: {
          paymentPeriod: pp,
        },
      })
      .afterClosed()
      .subscribe((updatePaymentPeriod: any) => {
        if (updatePaymentPeriod) {
          this.dialog
            .open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Are You Sure?',
                line1: 'This will change the payment information for this period. Please confirm.',
                buttonTextYes: 'Yes, Change',
                buttonTextNo: 'No, Cancel',
              },
              autoFocus: false,
            })
            .afterClosed()
            .subscribe(confirm => {
              if (confirm) {
                this.siteService.updatePaymentPeriod(this.site.id, updatePaymentPeriod).subscribe(() => {
                  this.store.dispatch(setActiveSite({ id: this.site.id }));
                });
              }
            });
        }
      });
  }

  isFutureVariablePeriod(element) {
    return element.isVariablePeriod && moment().isBefore(element.startDate);
  }

  getEstimatedPaymentDate(element: PaymentPeriod) {
    const [year, month,] = element.endDate.toString().split("-").map(Number);
    return new Date(year, month - 1 + 2, 15);
  }
}
